import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { usePageMeta } from "@/lib/meta";
import { Overline } from "@/components/Reveal";
import { Field, inputCls } from "@/components/forms";
import { Monogram } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function detailToMessage(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  usePageMeta("Admin Sign In | Credible Presence", "Private administration area.");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      navigate("/admin/dashboard");
    } catch (err) {
      setError(detailToMessage(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div data-testid="admin-login-page" className="flex min-h-[90vh] items-center justify-center bg-[#F7F5F1] px-6 pt-20">
      <form onSubmit={submit} className="card-shadow-lg w-full max-w-md rounded-md bg-white p-8 sm:p-10">
        <div className="flex justify-center"><Monogram size={44} /></div>
        <div className="mt-6 text-center">
          <Overline>Private Area</Overline>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-[#26282E]">Administration</h1>
        </div>
        <div className="mt-8 space-y-6">
          <Field label="Email" htmlFor="admin-email">
            <input id="admin-email" type="email" data-testid="admin-email-input" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
          </Field>
          <Field label="Password" htmlFor="admin-password">
            <input id="admin-password" type="password" data-testid="admin-password-input" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </Field>
        </div>
        {error && (
          <p data-testid="admin-login-error" className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-login-submit-button"
          disabled={loading || !email || !password}
          className="mt-8 w-full rounded-md bg-[#2F5D8C] px-7 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:bg-[#254A6E] disabled:opacity-30"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
        <p className="mt-6 text-center">
          <Link to="/" data-testid="admin-back-home-link" className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A8D94] hover:text-[#26282E]">
            Return to site
          </Link>
        </p>
      </form>
    </div>
  );
}
